import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, Clock, Users, Phone, User, ArrowLeft } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import { Spinner } from "../components/common/UI";
import toast from "react-hot-toast";

const TIME_SLOTS = [
  "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
  "14:00", "14:30", "18:00", "18:30", "19:00", "19:30",
  "20:00", "20:30", "21:00", "21:30", "22:00",
];

export default function BookingPage() {
  const { slug } = useParams();
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    name: profile?.name || "",
    phone: profile?.phone || "",
    date: today,
    time: "19:00",
    guests: 2,
    notes: "",
  });

  useEffect(() => {
    supabase
      .from("restaurants")
      .select("id, name, slug, location, city, cover_url, logo_url, contact, is_open, opening_time, closing_time")
      .eq("slug", slug)
      .single()
      .then(({ data }) => {
        setRestaurant(data);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (profile) {
      setForm((f) => ({
        ...f,
        name: profile.name || f.name,
        phone: profile.phone || f.phone,
      }));
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from("bookings").insert({
        restaurant_id: restaurant.id,
        user_id: user?.id || null,
        customer_name: form.name,
        customer_phone: form.phone,
        date: form.date,
        time: form.time,
        guests: parseInt(form.guests),
        notes: form.notes,
        status: "pending",
      });
      if (error) throw error;
      toast.success("Table booked! Restaurant will confirm shortly.");
      navigate("/my-bookings");
    } catch (err) {
      toast.error(err.message || "Failed to book table");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <>
      <Navbar />
      <div className="flex justify-center py-24"><Spinner size="lg" /></div>
    </>
  );

  if (!restaurant) return (
    <>
      <Navbar />
      <div className="text-center py-20">
        <p className="text-gray-500">Restaurant not found.</p>
        <Link to="/restaurants" className="btn-primary mt-4 inline-block">Browse Restaurants</Link>
      </div>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Book a Table — {restaurant.name} — Khana Spot</title>
      </Helmet>
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 page-enter">
        {/* Back link */}
        <Link
          to={`/restaurant/${slug}`}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to {restaurant.name}
        </Link>

        {/* Restaurant header */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
          <div className="relative h-32 bg-gradient-to-br from-orange-100 to-amber-100">
            {restaurant.cover_url && (
              <img src={restaurant.cover_url} alt={restaurant.name} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-4 flex items-center gap-3">
              {restaurant.logo_url && (
                <img src={restaurant.logo_url} alt="" className="w-10 h-10 rounded-xl border-2 border-white object-cover" />
              )}
              <div>
                <h1 className="font-display text-xl font-bold text-white">{restaurant.name}</h1>
                <p className="text-orange-200 text-xs">{restaurant.location}, {restaurant.city}</p>
              </div>
            </div>
          </div>
          <div className="px-4 py-3 flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-orange-400" />
              {restaurant.opening_time} – {restaurant.closing_time}
            </span>
            <span className={`badge ${restaurant.is_open ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {restaurant.is_open ? "● Open Now" : "● Closed"}
            </span>
          </div>
        </div>

        {/* Booking form */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-display text-xl font-bold text-gray-900 mb-5">🪑 Reserve a Table</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    placeholder="Muhammad Ali"
                    className="input-field pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    required
                    placeholder="03XX-XXXXXXX"
                    className="input-field pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={form.date}
                  min={today}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  required
                  className="input-field pl-10"
                />
              </div>
            </div>

            {/* Time slots */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                Time *
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {TIME_SLOTS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, time: slot }))}
                    className={`py-2 text-sm font-medium rounded-xl border transition-all ${
                      form.time === slot
                        ? "bg-orange-500 text-white border-orange-500"
                        : "border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600"
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>

            {/* Guests */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Number of Guests *
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={form.guests}
                  onChange={(e) => setForm((f) => ({ ...f, guests: e.target.value }))}
                  className="input-field pl-10"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 15, 20].map((n) => (
                    <option key={n} value={n}>{n} {n === 1 ? "Guest" : "Guests"}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Special Requests (optional)
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
                placeholder="Birthday celebration, window seat, allergies..."
                className="input-field resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Booking...</>
              ) : (
                "🪑 Confirm Booking"
              )}
            </button>

            <p className="text-xs text-center text-gray-400">
              The restaurant will call you on <strong>{form.phone || "your number"}</strong> to confirm.
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}
